













































import Vue from 'vue';
import {Component, Watch} from 'vue-property-decorator';
import Layout from '@/components/Layout.vue';
import echarts from 'echarts';
import TabBar from '@/components/TabBar.vue';
import Icon from '@/components/Icon.vue';
import dayjs from 'dayjs';
import clone from '@/lib/clone';
import {twoDecimalPlaces} from '@/lib/decimal';
import Blank from '@/components/Blank.vue';

type Group = {
    tag: TagItem;
    amount: number;
    percentage: number;
}

@Component({
    components: {Blank, Icon, TabBar, Layout}
})
export default class Charts extends Vue {

    typeList: TabBarItem[] = [
        {name: '支出', value: '-'},
        {name: '收入', value: '+'}
    ];
    intervalList: TabBarItem[] = [
        {name: '周', value: 'week'},
        {name: '月', value: 'month'},
        {name: '年', value: 'year'}
    ];

    type: '+' | '-' = '-';
    interval: 'week' | 'month' | 'year' = 'week';

    get targetRecords(): RecordItem[] {
        const now = dayjs();
        return clone<RecordItem[]>(this.$store.state.recordList).filter(item => item.type === this.type)
            .filter(item => dayjs(item.createAt).isSame(now, this.interval));
    }

    get total() {
        const amounts = [...this.groupByInterval().values()];
        let sum = 0;
        for (let i = 0; i < amounts.length; i++) {
            sum += amounts[i];
        }
        return sum;
    }

    get average() {
        const now = dayjs();
        switch (this.interval) {
            case 'week':
                return twoDecimalPlaces(this.total / (now.day() + 1));
            case 'month':
                return twoDecimalPlaces(this.total / now.date());
            case 'year':
                return twoDecimalPlaces(this.total / (now.month() + 1));
            default:
                return 0;
        }
    }

    groupByInterval(): Map<string, number> {
        let result = new Map<string, number>();
        switch (this.interval) {
            case 'week':
                result = this.groupByWeek(this.targetRecords);
                break;
            case 'month':
                result = this.groupByMonth(this.targetRecords);
                break;
            case 'year':
                result = this.groupByYear(this.targetRecords);
                break;
        }
        return result;
    }

    groupByTag(): Group[] {
        const tags: string[] = [];
        let result: Group[] = [];
        let r: RecordItem;
        for (r of this.targetRecords) {
            const index = tags.indexOf(r.tag.name)
            if (index < 0) {
                tags.push(r.tag.name);
                result.push({tag: r.tag, amount: r.amount, percentage: 0});
            } else {
                result[index].amount += r.amount;
            }
        }
        for (let i = 0; i < result.length; i++) {
            result[i].percentage = twoDecimalPlaces(result[i].amount * 100 / this.total);
        }
        result = result.sort((b, a) => a.percentage - b.percentage);
        return result;
    }

    get days() {
        const [year, month] = [dayjs().year(), dayjs().month()];
        const d = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

        if ((year % 4 === 0 && year % 100 !== 0) || (year % 100 === 0 && year % 400 === 0)) {
            if (month === 1) {
                return 29;
            } else {
                return d[month];
            }
        } else {
            return d[month];
        }
    }

    groupByWeek(records: RecordItem[]): Map<string, number> {
        const keys = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
        const result = new Map<string, number>();
        let i: number;
        // 初始化
        for (i = 0; i < keys.length; i++) {
            result.set(keys[i], 0);
        }
        let r: RecordItem;
        for (r of records) {
            const key = keys[dayjs(r.createAt).day()];
            const amount = result.get(key) as number;
            result.set(key, amount + r.amount);
        }
        return result;
    }

    groupByMonth(records: RecordItem[]): Map<string, number> {
        const keys: string[] = [];
        const result = new Map<string, number>();
        let i: number;
        // 初始化
        for (i = 1; i < this.days; i++) {
            keys.push(i.toString());
        }
        for (i = 0; i < keys.length; i++) {
            result.set(keys[i], 0);
        }
        let r: RecordItem;
        for (r of records) {
            const key = dayjs(r.createAt).date().toString();
            const amount = result.get(key) as number;
            result.set(key, amount + r.amount);
        }
        return result;
    }

    groupByYear(records: RecordItem[]): Map<string, number> {
        const keys = ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月'];
        const result = new Map<string, number>();
        let i: number;
        // 初始化
        for (i = 0; i < keys.length; i++) {
            result.set(keys[i], 0);
        }
        let r: RecordItem;
        for (r of records) {
            const key = keys[dayjs(r.createAt).month()];
            const amount = result.get(key) as number;
            result.set(key, amount + r.amount);
        }
        return result;
    }

    toArray(value: number, length: number): number[] {
        const result: number[] = [];
        for (let i = 0; i < length; i++) {
            result.push(value);
        }
        return result;
    }


    draw(data: Map<string, number>) {
        // 提取变量
        const x = [...data.keys()];
        const y = [...data.values()];

        const figure = echarts.init(document.getElementById('figure') as HTMLDivElement);
        figure.setOption({
            grid: {
                top: '5%',
                bottom: '10%'
            },
            xAxis: {
                data: x,
                axisTick: {
                    interval: 0,
                    lineStyle: {
                        opacity: 0
                    }
                },
                axisLabel: {
                    interval: 0,
                    fontSize: 8,
                    color: '#999999'
                }
            },
            yAxis: {
                axisLine: {
                    lineStyle: {
                        opacity: 0
                    }
                },
                splitLine: {
                    lineStyle: {
                        opacity: 0
                    }
                },
                axisLabel: undefined,
                axisTick: undefined,
            },
            series: [{
                type: 'line',
                data: y,
            }, {
                name: '平均线',
                type: 'line',
                data: this.toArray(this.average, x.length),
                symbol: 'none',
                lineStyle: {
                    type: 'dashed',
                    color: '#999999',
                    width: 1,
                    opacity: 0.5
                }
            }, {
                name: '最大值',
                type: 'line',
                data: this.toArray(Math.max(...y), x.length),
                symbol: 'none',
                lineStyle: {
                    color: '#999999',
                    width: 1,
                    opacity: 0.5
                }
            }]
        });
    }

    mounted() {
        this.draw(this.groupByInterval());
    }

    @Watch('type')
    onTypeChang() {
        this.draw(this.groupByInterval());
    }

    @Watch('interval')
    onIntervalChange() {
        this.draw(this.groupByInterval());
    }
}
